import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MessageThread() {
  const { otherId } = useParams();
  const [params] = useSearchParams();
  const listingId = params.get("listing");
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const endRef = useRef();

  const refresh = async () => {
    const [o, m] = await Promise.all([
      api.get(`/users/${otherId}`).then((r) => r.data).catch(() => null),
      api.get(`/messages/with/${otherId}`).then((r) => r.data).catch(() => []),
    ]);
    setOther(o);
    setMessages(m);
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [otherId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setError("");
    try {
      const payload = { recipient_id: otherId, body };
      if (listingId) payload.listing_id = listingId;
      const { data } = await api.post("/messages", payload);
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12">
      <Link to="/dashboard" className="label-overline hover:text-wax-red">← back to dashboard</Link>

      <div className="mt-6 mb-8">
        <p className="label-overline">Correspondence</p>
        <h1 className="font-display text-4xl mt-2">
          With <em className="text-wax-red">{other?.name || "…"}</em>
        </h1>
        {other?.location && <p className="font-body text-ink-iron mt-1">{other.location} · {other.region}</p>}
      </div>

      <div className="parchment-card max-h-[60vh] overflow-y-auto space-y-4" data-testid="thread-messages">
        {messages.length === 0 ? (
          <p className="marginalia">No letters yet. Begin with a quiet word.</p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] border p-4 ${mine ? "bg-parchment-bone border-rule" : "bg-parchment-faded border-rule"}`}>
                  <p className="label-overline mb-1">{mine ? "You" : m.sender_name}</p>
                  <p className="font-body whitespace-pre-wrap leading-relaxed">{m.body}</p>
                  <p className="text-xs text-ink-marginalia italic mt-2">{new Date(m.created_at).toLocaleString()}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-6" data-testid="thread-form">
        <textarea
          data-testid="thread-body"
          className="field"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Compose your letter…"
        />
        {error && <p className="text-wax-red text-sm italic mt-2">{error}</p>}
        <div className="flex justify-end mt-3">
          <button data-testid="thread-send" className="wax-button">Seal & send</button>
        </div>
      </form>
    </main>
  );
}
